import { max } from 'd3-array';
import { entries, map, set } from 'd3-collection';
import { geoConicConformal, geoPath } from 'd3-geo';
import { Component, h } from 'preact';
import { feature, merge } from 'topojson-client';

import County from '../components/County';
import ResultsMapLegend from './ResultsMapLegend';
import { NEUTRAL, TIE } from '../utils/primary-colors';
import tooltipGenerator from '../utils/tooltip-generator';
import debounce from '../utils/debounce';

import fetchJSON from '../utils/fetch-json';

const navigator = window.navigator;
const isMobile =
  /Android/i.test(navigator.userAgent) ||
  /iPhone|iPad|iPod/i.test(navigator.userAgent);

class ResultsMap extends Component {
  constructor() {
    super();

    this.state = {
      bbox: null,
      shapes: null,
      places: null,
      state: null,
      isLoaded: false,
    };

    this.onHover = this.onHover.bind(this);
    this.onMouseOut = this.onMouseOut.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.getContainerWidth = this.getContainerWidth.bind(this);

    this.tooltip = document.querySelector('.map__tooltip');
  }

  onHover(fips, e) {
    if (isMobile) return;
    const county = this.countiesMap.get(fips);
    if (!county) return;

    this.tooltip.innerHTML = tooltipGenerator(
      county,
      this.candidateMap,
      this.props.colorMap
    );
  }

  onMouseOut() {
    if (isMobile) return;
    this.tooltip.style.display = 'none';
  }

  onMouseMove(e) {
    if (isMobile) return;
    const pageX = e.pageX;
    const pageY = e.pageY;
    const clientX = e.clientX;
    const clientY = e.clientY;

    this.tooltip.style.display = 'block';
    const { height, width } = this.tooltip.getBoundingClientRect();

    let leftPosition;

    if (clientX + width + 20 > window.innerWidth) {
      leftPosition = pageX + 10 - width;
    } else {
      leftPosition = pageX + 10;
    }

    let topPosition;

    if (clientY + height + 20 > window.innerHeight) {
      topPosition = pageY + 10 - height;
    } else {
      topPosition = pageY + 10;
    }

    this.tooltip.setAttribute(
      'style',
      `display: block; left: ${leftPosition}px; top: ${topPosition}px;`
    );
  }

  getContainerWidth() {
    const base = this.base;
    const style = getComputedStyle(this.base, null);
    const width = parseInt(style.getPropertyValue('width'), 10);

    return width;

    // this.setState({ containerWidth: width });
  }

  componentWillReceiveProps(nextProps) {
    this.candidateMap = map(nextProps.candidates, d => d.id);
    this.countiesMap = map(nextProps.counties, d => d.fips);
  }

  componentDidMount() {
    // this.debouncedSetContainerWidth();
    this.candidateMap = map(this.props.candidates, d => d.id);
    this.countiesMap = map(this.props.counties, d => d.fips);

    // this.onResize = window.addEventListener(
    //   'resize',
    //   this.debouncedSetContainerWidth
    // );
    // window.addEventListener(
    //   'orientationchange',
    //   this.debouncedSetContainerWidth
    // );

    fetchJSON(`${process.env.PUBLIC_PATH}assets/geo/counties-places.json`).then(
      geo => {
        const bbox = geo.bbox;
        const shapes = feature(geo, geo.objects.counties);
        const places = feature(geo, geo.objects.places);
        const state = merge(geo, geo.objects.counties.geometries);
        const isLoaded = true;

        this.setState({
          bbox,
          isLoaded,
          places,
          shapes,
          state,
        });
      }
    );
  }

  // componentDidUnmount() {
  //   window.removeEventListener('resize', this.debouncedSetContainerWidth);
  //   window.removeEventListener(
  //     'orientationchange',
  //     this.debouncedSetContainerWidth
  //   );
  // }

  // shouldComponentUpdate(nextProps) {
  //   console.log(
  //     nextProps.contest.precincts_reporting,
  //     this.props.contest.precincts_reporting
  //   );
  //   return (
  //     nextProps.contest.precincts_reporting ===
  //     this.props.contest.precincts_reporting
  //   );
  // }

  render(
    { candidates, colorMap, contest, counties },
    { bbox, isLoaded, places, shapes, state }
  ) {
    if (!isLoaded) return <div class="results-map" />;
    const containerWidth = this.getContainerWidth();

    const margin = { top: 0, right: 0, bottom: 0, left: 0 };

    const width = containerWidth - margin.left - margin.right;

    const heightRatio = (bbox[3] - bbox[1]) / (bbox[2] - bbox[0]);
    const height = width * heightRatio;

    const projection = geoConicConformal()
      .parallels([32 + 8 / 60, 33 + 58 / 60])
      .rotate([98 + 30 / 60, 0]);
    projection.fitSize([width, height], shapes);

    const path = geoPath()
      .projection(projection)
      .pointRadius(3);

    const countiesMap = this.countiesMap;
    const candidateOnMap = set();

    return (
      <div class="results-map">
        <svg width={width} height={height}>
          <g transform={`translate(${margin.left},${margin.top})`}>
            <g class="counties">
              {shapes.features.map(shape => {
                const fips = shape.properties.fips.slice(2);
                const county = countiesMap.get(fips);

                let color;

                if (county.total_votes <= 0) {
                  color = NEUTRAL;
                } else {
                  const sortedResults = entries(county.results).sort(
                    (a, b) => b.value - a.value
                  );

                  const leader = sortedResults[0];
                  const second = sortedResults[1];

                  if (leader.value === second.value) {
                    color = TIE;
                  } else {
                    const leadingId = leader.key;
                    candidateOnMap.add(leadingId);
                    color = colorMap.get(leadingId);
                  }
                }

                return (
                  <County
                    key={fips}
                    color={color}
                    feature={shape}
                    geoPath={path}
                    results={county}
                    onHover={this.onHover}
                    onMouseOut={this.onMouseOut}
                    onMouseMove={this.onMouseMove}
                  />
                );
              })}
            </g>
            <g class="state">
              <path d={path(state)} />
            </g>
            <g class="cities">
              {places.features.map(place => (
                <path class="place-circle" d={path(place)} />
              ))}
            </g>
            <g class="place-labels">
              {places.features.map(place => (
                <text
                  class="place-background-label"
                  transform={`translate(${projection([
                    place.geometry.coordinates[0],
                    place.geometry.coordinates[1],
                  ])})`}
                  dx={place.properties.dx}
                  dy={place.properties.dy}
                  text-anchor={place.properties.anchor}
                >
                  {place.properties.name}
                </text>
              ))}
              {places.features.map(place => (
                <text
                  class="place-label"
                  transform={`translate(${projection([
                    place.geometry.coordinates[0],
                    place.geometry.coordinates[1],
                  ])})`}
                  dx={place.properties.dx}
                  dy={place.properties.dy}
                  text-anchor={place.properties.anchor}
                >
                  {place.properties.name}
                </text>
              ))}
            </g>
          </g>
        </svg>
        {contest.precincts_reporting > 0 && (
          <ResultsMapLegend
            candidates={candidates.filter(({ id }) => candidateOnMap.has(id))}
            colorMap={colorMap}
          />
        )}
      </div>
    );
  }
}

export default ResultsMap;



// WEBPACK FOOTER //
// ./componentsV2/ResultsMap.js